import { useEffect, useState } from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import dataService from "@/app/services/dataService";
import { ReturnOrderItem } from "@/app/interfaces/ReturnOrderItem";
import ReturnItemsGrid from "./ReturnItemsGrid";
import QuickReturnDialog from "./QuickReturnDialog";
import ThankYouDialog from "./ThankYouDialog";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import QuickReturnPayload from "@/app/interfaces/payload/QuickReturnPaylaod";

const ReturnOrderPage = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const orderId = params.get("orderId");

  const [items, setItems] = useState<ReturnOrderItem[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>();
  const [orderDate, setOrderDate] = useState<string>();
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [quickReturnOpen, setQuickReturnOpen] = useState<boolean>(false);
  const [thankYouOpen, setThankYouOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadPagedItems();
  }, [page, pageSize]);

  const loadPagedItems = async () => {
    if (!orderId) return;

    const response = await dataService.fetchReturnOrderItems(orderId, {
      page,
      pageSize,
      onlySelected: false,
    });

    if (response?.data && response?.success) {
      const loadedItems = response.data.items ?? [];

      setItems(loadedItems);
      setOrderNumber(response.data.orderNumber);
      setOrderDate(response.data.orderDate);
      setTotalCount(response.data.totalCount);
    }
  };

  const handleItemChange = async (id: number, checked: boolean) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isSelected: checked } : item
      )
    );

    try {
      await dataService.changeReturnItemSelection(
        Number(orderId),
        checked,
        id.toString()
      );
    } catch (error) {
      console.error("Selection update failed:", error);

      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isSelected: !checked } : item
        )
      );
    }
  };

  const handleSelectAllChange = async (checked: boolean) => {
    setSelectAll(checked);
    setItems((prev) => prev.map((item) => ({ ...item, isSelected: checked })));

    try {
      await dataService.changeReturnItemSelection(
        Number(orderId),
        checked,
        null
      );
    } catch (error) {
      console.error("Selection update failed:", error);
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      setSelectAll(false);
      return;
    }

    const allSelected = items.every((item) => item.isSelected);
    setSelectAll(allSelected);
  }, [items]);

  const handleContinue = () => {
    navigate(`/order/ReturnItemsDetails?orderId=${orderId}`);
  };

  const handleQuickReturnSubmit = async (note: string, palletFlag: boolean) => {
    const payload: QuickReturnPayload = {
      orderId: Number(orderId),
      note,
      palletFlag,
    };

    const result = await dataService.sendQuickReturnRequest(payload);

    if (result) {
      setQuickReturnOpen(false);
      setThankYouOpen(true);
    } else {
      alert(result);
    }
  };

  return (
    <Box>
      <div id="ph-title" className="text-center page-title">
        <h1>Return Request</h1>
      </div>
      <Box sx={{ padding: "0px 20px", marginTop: "-30px" }}>
        <Typography
          onClick={() => navigate("/order/history")}
          sx={{
            color: "#54afe2",
            fontSize: "14px",
            fontFamily: 'normal 14px "Montserrat", Verdana, sans-serif',
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          {"< Back to All Orders"}
        </Typography>

        <Typography
          fontSize={"28px"}
          fontWeight={600}
          textAlign={"center"}
          color={"#494440"}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
        >
          Select Items To Return
        </Typography>

        <Grid container marginBottom={4}>
          <Grid item md={7} xs={12}>
            <Typography
              fontSize={"110%"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
            >
              <strong> Order #{orderNumber} </strong> — {items.length} Products
              | Ordered {orderDate}
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: "0px 3px 6px rgba(0,0,0,0.16)",
                backgroundColor: "#fff",
              }}
            >
              <ReturnItemsGrid
                items={items}
                selectAll={selectAll}
                onItemChange={handleItemChange}
                onSelectAllChange={handleSelectAllChange}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </Box>
          </Grid>

          <Grid
            item
            md={5}
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: { xs: "20px", md: "0" },
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#494440",
                  fontFamily: 'normal 14px "Montserrat", Verdana, sans-serif; ',
                  fontWeight: 600,
                }}
              >
                Are all items you wish to return selected?
              </Typography>
              <Button
                disabled={!items.some((x) => x.isSelected)}
                className="blueButtonOutline filledBlueButton"
                onClick={handleContinue}
                sx={{ margin: "20px auto", height: "44px", minWidth: "270px" }}
              >
                Continue
              </Button>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  lineHeight: 1.6,
                }}
              >
                Need more assistance?
                <br />
                Contact{" "}
                <span
                  style={{
                    color: "#54afe2", //
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                  onClick={() => setQuickReturnOpen(true)}
                >
                  Branch.
                </span>
              </Typography>
            </Box>
            <Typography
              maxWidth={"300px"}
              fontSize={"11px"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              mt={2}
            >
              <strong>Please Note:</strong> <br />
              Returns may be subject to surcharges and fees. Special order
              products may not be returned. Return quantities will be verified,
              and return amounts will be calculated by your branch upon
              approval.
            </Typography>
          </Grid>
        </Grid>

        <QuickReturnDialog
          open={quickReturnOpen}
          onClose={() => setQuickReturnOpen(false)}
          onSubmit={handleQuickReturnSubmit}
        />

        <ThankYouDialog
          open={thankYouOpen}
          onClose={() => setThankYouOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default ReturnOrderPage;
